// Pure grouping logic for the "one complete route per day" PDF export.
// No I/O: takes already-saved itinerary data and returns per-day sections with
// their own ordered stops, numbering, map points and summary. Never invents
// coordinates — points without them stay in the list and are flagged.

import { guessCategoryFromText } from "./negative-preferences";

export type DayPlanStop = {
  name: string;
  day?: number | null;
  lat?: number | null;
  lng?: number | null;
  placeId?: string | null;
  blurb?: string | null;
  note?: string | null;
  arrive?: string | null;
  leave?: string | null;
  /** Travel minutes from the previous stop of the SAME day. */
  transitMinutes?: number | null;
  /** Travel distance in metres from the previous stop of the same day. */
  transitMeters?: number | null;
};

export type DayPlanBreak = {
  kind: "break";
  day?: number | null;
  label: string;
  start?: string | null;
  end?: string | null;
  lat?: number | null;
  lng?: number | null;
  /** Sorting hint: place the break after this many stops of the day. */
  afterStopIndex?: number | null;
};

export type DayPlanEntry = ({ kind: "stop" } & DayPlanStop) | DayPlanBreak;

export type DaySectionStop = DayPlanStop & {
  /** 1-based order within the day. */
  index: number;
  mapped: boolean;
  isStart: boolean;
  isEnd: boolean;
};

export type DaySection = {
  day: number;
  title: string;
  dateLabel?: string;
  stops: DaySectionStop[];
  breaks: DayPlanBreak[];
  /**
   * Ordem CRONOLÓGICA real do dia (paragens e pausas intercaladas). O PDF deve
   * usar esta lista: listar todas as paragens e só depois as pausas colocava o
   * restaurante do almoço no fim do dia.
   */
  timeline: Array<{ kind: "stop"; stop: DaySectionStop } | { kind: "break"; brk: DayPlanBreak }>;
  /** Ordered coordinates for this day's own map/route. */
  mapPoints: Array<{ lat: number; lng: number }>;
  venuePoints: Array<{ lat: number; lng: number; kind: "lunch" | "dinner" | "hotel" }>;
  unmappedNames: string[];
  totalDistanceMeters: number | null;
  totalTravelMinutes: number | null;
  empty: boolean;
};

function hasCoords(s: { lat?: number | null; lng?: number | null }): boolean {
  return (
    typeof s.lat === "number" &&
    Number.isFinite(s.lat) &&
    typeof s.lng === "number" &&
    Number.isFinite(s.lng) &&
    !(s.lat === 0 && s.lng === 0)
  );
}

function venueKind(label: string): "lunch" | "dinner" | "hotel" {
  const l = label.toLowerCase();
  if (l.includes("hotel") || l.includes("dormir") || l.includes("alojamento")) return "hotel";
  if (l.includes("jantar") || l.includes("dinner")) return "dinner";
  return "lunch";
}

export type BuildDaySectionsInput = {
  entries: DayPlanEntry[];
  /** Optional per-day labels, keyed by day number. */
  titles?: Record<number, string>;
  dateLabels?: Record<number, string>;
  /** Force these day numbers to exist even when they have no stops. */
  expectedDays?: number[];
  /**
   * Exclusões duras já detetadas no pedido (ex.: "não quero restaurantes").
   * Passe final e defensivo: nenhuma paragem/pausa excluída pode chegar ao PDF.
   * Uma pausa excluída (ex.: refeição num restaurante indesejado) vira uma
   * pausa neutra sem nome/link em vez de desaparecer do horário.
   */
  excludedCategories?: string[];
};

function isExcludedCategoryText(text: string | null | undefined, excluded: string[]): boolean {
  if (!text || excluded.length === 0) return false;
  const guess = guessCategoryFromText(text);
  return !!guess && excluded.includes(guess);
}

function sanitizeEntries(entries: DayPlanEntry[], excluded: string[]): DayPlanEntry[] {
  if (excluded.length === 0) return entries;
  return entries.flatMap((entry): DayPlanEntry[] => {
    if (entry.kind === "stop") {
      return isExcludedCategoryText(entry.name, excluded) ? [] : [entry];
    }
    if (isExcludedCategoryText(entry.label, excluded)) {
      // Pausa excluída (ex.: refeição num restaurante indesejado) vira pausa
      // neutra: sem nome/link, apenas o intervalo de horário mantém-se.
      return [{ ...entry, label: "Pausa livre", lat: undefined, lng: undefined }];
    }
    return [entry];
  });
}

export function buildDaySections(input: BuildDaySectionsInput): DaySection[] {
  const entries = sanitizeEntries(input.entries, input.excludedCategories ?? []);
  const dayNumbers = new Set<number>();
  for (const e of entries) dayNumbers.add(Math.max(1, Math.trunc(e.day ?? 1)));
  for (const d of input.expectedDays ?? []) dayNumbers.add(Math.max(1, Math.trunc(d)));
  if (dayNumbers.size === 0) dayNumbers.add(1);

  const sections: DaySection[] = [];
  for (const day of Array.from(dayNumbers).sort((a, b) => a - b)) {
    const forDay = entries.filter((e) => Math.max(1, Math.trunc(e.day ?? 1)) === day);
    const rawStops = forDay.filter((e): e is { kind: "stop" } & DayPlanStop => e.kind === "stop");
    const breaks = forDay.filter((e): e is DayPlanBreak => e.kind === "break");

    const stops: DaySectionStop[] = rawStops.map((s, i) => ({
      ...s,
      index: i + 1,
      mapped: hasCoords(s),
      isStart: i === 0,
      isEnd: i === rawStops.length - 1,
    }));

    // Reconstrói a ordem original de entrada, agora com as paragens numeradas.
    const byRef = new Map<object, DaySectionStop>();
    rawStops.forEach((raw, i) => byRef.set(raw, stops[i]));
    const timeline: DaySection["timeline"] = [];
    for (const entry of forDay) {
      if (entry.kind === "stop") {
        const stop = byRef.get(entry);
        if (stop) timeline.push({ kind: "stop", stop });
      } else {
        timeline.push({ kind: "break", brk: entry });
      }
    }
    timeline.sort((a, b) => {
      const ta = a.kind === "stop" ? a.stop.arrive : a.brk.start;
      const tb = b.kind === "stop" ? b.stop.arrive : b.brk.start;
      if (!ta || !tb) return 0; // sem horas: mantém a ordem de entrada
      return ta.localeCompare(tb);
    });

    const mapPoints = stops
      .filter((s) => s.mapped)
      .map((s) => ({ lat: s.lat as number, lng: s.lng as number }));

    const venuePoints = breaks
      .filter((b) => hasCoords(b))
      .map((b) => ({ lat: b.lat as number, lng: b.lng as number, kind: venueKind(b.label) }));

    const distanceValues = stops
      .slice(1)
      .map((s) => s.transitMeters)
      .filter((v): v is number => typeof v === "number");
    const travelValues = stops
      .slice(1)
      .map((s) => s.transitMinutes)
      .filter((v): v is number => typeof v === "number");

    sections.push({
      day,
      title: input.titles?.[day] ?? `Dia ${day}`,
      ...(input.dateLabels?.[day] ? { dateLabel: input.dateLabels[day] } : {}),
      stops,
      breaks,
      timeline,
      mapPoints,
      venuePoints,
      unmappedNames: stops.filter((s) => !s.mapped).map((s) => s.name),
      totalDistanceMeters: distanceValues.length ? distanceValues.reduce((a, b) => a + b, 0) : null,
      totalTravelMinutes: travelValues.length ? travelValues.reduce((a, b) => a + b, 0) : null,
      empty: stops.length === 0,
    });
  }
  return sections;
}

export function dayRouteUrl(
  points: Array<{ lat: number; lng: number }>,
  travelmode: "walking" | "driving" | "transit" | "bicycling",
): string | undefined {
  if (points.length < 2) return undefined;
  const coord = (p: { lat: number; lng: number }) => `${p.lat},${p.lng}`;
  const params = new URLSearchParams({
    api: "1",
    origin: coord(points[0]),
    destination: coord(points[points.length - 1]),
    travelmode,
  });
  const waypoints = points.slice(1, -1).slice(0, 23).map(coord).join("|");
  if (waypoints) params.set("waypoints", waypoints);
  return `https://www.google.com/maps/dir/?${params.toString()}`;
}

export function formatDistance(meters: number | null): string | undefined {
  if (meters == null) return undefined;
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

export function formatDuration(minutes: number | null): string | undefined {
  if (minutes == null) return undefined;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h > 0 ? `${h} h ${m} min` : `${m} min`;
}
